import { type QueryClient, type QueryFunctionContext, useQuery, useQueryClient } from '@tanstack/react-query'

import type { QueryOpts, QueryResult } from '../types/reactQuery.ts'
import { queryClient as appQueryClient } from './queryClient.ts'

export interface InvalidateQueryOptions {
  /** Elementos adicionales de la clave: invalida `[baseKey, ...extraQueryKeys]`. */
  extraQueryKeys?: readonly unknown[]
  /** `true` solo invalida la clave exacta; por defecto invalida todas las que empiezan con ella. */
  exact?: boolean
  /** Cliente a usar; por defecto el cliente único de la aplicación. */
  queryClient?: QueryClient
}

/**
 * Invalida las queries cuya clave empieza con `[baseKey, ...extraQueryKeys]`.
 * Se puede llamar desde cualquier lugar (onSuccess de mutaciones, stores, servicios),
 * sin montar el hook del listado.
 * @example invalidateQuery(KEY_PEDIDO_LISTADO)
 * @example invalidateQuery(KEY_PEDIDO_LISTADO, { extraQueryKeys: [entidad], exact: true })
 */
export const invalidateQuery = (
  baseKey: string,
  { extraQueryKeys = [], exact = false, queryClient = appQueryClient }: InvalidateQueryOptions = {},
) => queryClient.invalidateQueries({ queryKey: [baseKey, ...extraQueryKeys], exact })

/**
 * Hook base para queries: arma la clave `[baseKey, ...queryKeys]` y expone `invalidate`.
 * Los defaults propios de cada servicio (staleTime, etc.) se pasan en `options` antes del spread.
 * Usar solo donde se muestran los datos; para invalidar sin mostrarlos, usar `invalidateQuery`.
 * @example
 * export const useXListado = (options?: QueryOpts<X[]>) =>
 *   useApiQuery(KEY_X_LISTADO, (ctx) => apiXListado({ signal: ctx.signal }), { staleTime: 60_000, ...options })
 */
export const useApiQuery = <TData>(
  baseKey: string,
  queryFn: (ctx: QueryFunctionContext) => Promise<TData>,
  options: QueryOpts<TData> = {},
): QueryResult<TData> => {
  const queryClient = useQueryClient()
  const { queryKeys = [], ...restOptions } = options

  const query = useQuery({ queryKey: [baseKey, ...queryKeys], queryFn, ...restOptions })

  const invalidate = (exact = false) =>
    invalidateQuery(baseKey, { extraQueryKeys: exact ? queryKeys : [], exact, queryClient })

  return { ...query, invalidate }
}
